import { DateRangeSelection, DynamicDates, dynamicDatesToDateRangeSelectionMap, getDateRangeSelection, getHoursAndMinutes, getNumberOfDays, isDateRangeValid, parseDate } from "csi/utility/dateUtility";
import { Locale, format } from "date-fns";
import { ILocaleProvider, defaultDateFormat, defaultTimeFormat } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { debounce, invert } from "lodash";
import { action, makeObservable, observable } from "mobx";
import React, { createContext, useContext } from "react";
import { DateRange as DateRangeDayPicker } from "react-day-picker";
import { Operator } from "src/c8-platform/type/operators";
import { C8DateRange } from "./DateRange";

export type DateRangeTimePickerMode = "range" | "single";

export type DateRangeTimePickerOnRangeChange = (range: C8DateRange, dds?: DynamicDates, n?: number) => void;

export type DateRangeTimePickerOnViolation = (isValid: boolean) => void;

export function getDynamicDates(selectedOperatorId?: Operator): ComponentItem[] {
	const ci: ComponentItem[] = [
		{ id: "3", res: DynamicDates.OneWeekAgo },
		{ id: "4", res: DynamicDates.OneWeekFromNow },
		{ id: "5", res: DynamicDates.OneMonthAgo },
		{ id: "6", res: DynamicDates.OneMonthFromNow },
		{ id: "7", res: DynamicDates.NumberOfDaysAgo },
		{ id: "8", res: DynamicDates.NumberOfDaysFromNow },
	];

	if (selectedOperatorId === Operator.EQUALS) {
		ci.unshift({ id: "2", res: DynamicDates.Yesterday });
		ci.unshift({ id: "1", res: DynamicDates.Tomorrow });
	} else if (selectedOperatorId === Operator.WITHIN) {
		ci.unshift({ id: "2", res: DynamicDates.ThePastDay });
		ci.unshift({ id: "1", res: DynamicDates.TheNextDay });
	}
	
	if (selectedOperatorId !== Operator.WITHIN) {
		ci.unshift({ id: "0", res: DynamicDates.Today });
	}

	return ci;
}

export interface DateRangeTimePickerProps {
	/** initial range to start the picker with */
	initialRange?: C8DateRange;
	/** callback function for the range change */
	onRangeChange: DateRangeTimePickerOnRangeChange;
	/** callback function for the violation */
	onViolationChange: DateRangeTimePickerOnViolation;
	/** mode, for now, use "range" or "single" */
	mode: DateRangeTimePickerMode;
	/** selected operator id */
	selectedOperatorId?: Operator;
	/** flag to show or hide time slots */
	showTime?: boolean;
}

export const MIN_YEAR = 1900;
export const MAX_YEAR = 2100;

/** store for managing state and logic of DateRangeTimePicker component */
export class DateRangeTimePickerStore {
	/** HTML element used as the anchor for the picker popper */
	@observable anchorEl: HTMLElement | null = null;

	/** date fns locale */
	readonly dateFnsLocale: Locale;

	/** date format */
	private readonly dateFormat: string;

	/** string representing a dynamic date input */
	@observable dynamicDateString: DynamicDates;

	/** string representing an invalid date input */
	@observable invalidDateString: string = "";

	/** locale provider */
	private readonly loc: ILocaleProvider;

	/** date range time picker mode */
	@observable mode: DateRangeTimePickerMode;

	/** number of days from now in dynamic date */
	@observable numberOfDays: number = 1;

	/** number of days value in box */
	@observable numberOfDaysValue: string = "";

	/** on range change callback */
	onRangeChange: DateRangeTimePickerOnRangeChange;

	/** on violation callback */
	onViolationChange: DateRangeTimePickerOnViolation;

	/** string representing a preset date input */
	@observable preSetDateString: string = "";

	/** currently selected date range */
	@observable.ref range: C8DateRange = new C8DateRange();

	/** selected operator id */
	@observable selectedOperatorId: Operator;

	/** selected tab index for date range picker panel */
	@observable selectedTabIndex: number = 0;

	/** boolean to show time */
	@observable showTime: boolean;

	/** time format */
	private readonly timeFormat: string;

	/** reference to the time slot HTMLDivElement */
	timeSlotToRef: React.RefObject<HTMLDivElement>;

	constructor(
		initialRange: C8DateRange | null,
		loc: ILocaleProvider,
		mode: "range" | "single",
		onRangeChange: (range: C8DateRange) => void,
		onViolationChange: DateRangeTimePickerOnViolation,
		selectedOperatorId: Operator,
		showTime: boolean,
	) {
		makeObservable(this);
		// if the selected operator is EQ, then range.to should be null
		if (selectedOperatorId === Operator.EQUALS) {
			initialRange?.setToDate();
		}
		this.range = initialRange;
		const dateRangeSelection = getDateRangeSelection(initialRange?.toArray);
		const dateRangeSelectionsExistsAndIsNotCustom = dateRangeSelection && dateRangeSelection !== DateRangeSelection.CUSTOM;
		const nonCustomDateRangeSelectionAndNonRange = selectedOperatorId !== Operator.RANGE && dateRangeSelectionsExistsAndIsNotCustom;
		if (selectedOperatorId === Operator.WITHIN || nonCustomDateRangeSelectionAndNonRange) {
			this.dynamicDateString = invert(dynamicDatesToDateRangeSelectionMap)[dateRangeSelection] as DynamicDates;
			this.selectedTabIndex = 1;
		}
		this.onViolationChange = onViolationChange;
		this.selectedOperatorId = selectedOperatorId;
		this.numberOfDays = getNumberOfDays(initialRange?.toArray) ?? 1;
		this.numberOfDaysValue = `${this.numberOfDays}`;
		this.loc = loc;
		this.mode = mode;
		this.onRangeChange = onRangeChange;
		this.timeSlotToRef = React.createRef<HTMLDivElement>();
		this.showTime = showTime;
		this.dateFormat = loc.getDatePattern() || defaultDateFormat;
		this.timeFormat = loc.getTimePattern() || defaultTimeFormat;
	}

	/** TODO: this is needed to useLocalStore hook, maybe will need to add to it later */
	onCleanup = (store: DateRangeTimePickerStore): void => null;

	/** debounced function so that the user can make multiple quick mods to the date string */
	debouncedStringToDateRange = debounce((inputString: string): void => {
		this.stringToDateRange(inputString);
	}, 500);

	/** convert the date range to a string */
	get dateRangeToString(): string {
		const { range, dateStringFormat } = this;
		let s = "";
		if (!range) {
			return s;
		}
		if (range.fromEpochMillis) {
			s = format(range.fromEpochMillis, dateStringFormat);
		}
		if (range.toEpochMillis) {
			s += " - " + format(range.toEpochMillis, dateStringFormat);
		}
		return s;
	};

	/** date string format */
	get dateStringFormat(): string {
		const timeFormat = " " + this.timeFormat;
		return `${this.dateFormat}${this.showTime ? timeFormat : ""}`;
	}

	/** disable hours and minutes on from */
	get disabledFromTime(): boolean {
		return !this.range?.fromEpochMillis;
	}

	/** disable hours and minutes on to */
	get disabledToTime(): boolean {
		return !this.range?.toEpochMillis;
	}

	/** display value to use for the text field */
	get displayValue(): string {
		const { dynamicDateString, loc } = this;
		if (dynamicDateString) {
			return loc.resource(dynamicDateString) || "";
		}
		return this.preSetDateString || this.invalidDateString || this.dateRangeToString || "";
	}

	/** getter for dynamic dates */
	get dynamicDates(): ComponentItem[] {
		return getDynamicDates(this.selectedOperatorId);
	}

	/** getter for hide dynamic dates */
	get hideDynamicDates(): boolean {
		return this.selectedOperatorId === Operator.RANGE;
	}

	/** getter for hide fixed dates */
	get hideFixedDates(): boolean {
		return this.selectedOperatorId === Operator.WITHIN;
	}

	/** get if number of days from now is selected */
	get isNumberOfDaysSelected(): boolean {
		const { dynamicDateString } = this;
		return dynamicDateString === DynamicDates.NumberOfDaysFromNow ||
			dynamicDateString === DynamicDates.NumberOfDaysAgo;
	}

	/** flag indicating whether the picker is in range mode or single date mode */
	get isRangeMode(): boolean {
		return this.mode === "range";
	}

	/** get whether there is an error in the number of days from now input */
	get isNumberOfDaysError(): boolean {
		return !isNaN(this.numberOfDays);
	}

	/** selected time from */
	get selectedTimeSlotFrom(): string {
		return getHoursAndMinutes(this.range?.fromDate);
	}

	/** selected time to */
	get selectedTimeSlotTo(): string {
		return getHoursAndMinutes(this.range?.toDate);
	}
	
	/** getter for show tabs */
	get showTabs(): boolean {
		return !this.hideDynamicDates && !this.hideFixedDates;
	}

	/** handle the selected tab index change */
	@action handleTabChange = (event: React.SyntheticEvent<Element, Event>, value: number): void => {
		this.dynamicDateString = null;
		this.setRange(null);
		this.selectedTabIndex = value;
	};

	/** close the popper holding the date range time picker */
	@action closePopper = (): void => {
		this.anchorEl = null;
	};

	/** open the popper holding the date range time picker */
	@action openPopper = (e: React.MouseEvent<HTMLInputElement>): void => {
		this.anchorEl = e.currentTarget;
	};

	/** set the current date range and trigger the onRangeChange callback */
	@action setRange = (newRange: C8DateRange | null): void => {
		this.range = newRange;
		this.onRangeChange(newRange);
		this.setPreSetDateString("");
		this.setInvalidDateString("");
		this.setDynamicDateString(null);
	};

	/** set the selected tab index for this rule */
	@action setSelectedTabIndex = (n: number): void => {
		this.selectedTabIndex = n;
	};

	/** set the anchor element for the picker popper */
	@action setAnchorEl = (newAnchorEl: HTMLElement | null): void => {
		this.anchorEl = newAnchorEl;
	};

	/** set the string that represents a dynamic date input */
	@action setDynamicDateString = (newDynamicDateString: DynamicDates, callRangeChange?: boolean): void => {
		this.dynamicDateString = newDynamicDateString;
		if (callRangeChange) {
			this.onRangeChange(null, newDynamicDateString, this.isNumberOfDaysSelected ? this.numberOfDays : null);
			this.numberOfDaysValue = `${this.numberOfDays}`;
			this.closePopper();
		}
	};

	/** set the string that represents an invalid date input */
	@action setInvalidDateString = (newInvalidDateString: string): void => {
		const oldValue = this.invalidDateString;
		this.invalidDateString = newInvalidDateString;
		if ((!oldValue && !!newInvalidDateString) || (!!oldValue && !newInvalidDateString)) {
			this.onViolationChange(!newInvalidDateString);
		}
	};

	/** set number of days from now */
	@action setNumberOfDays = (n: string, isBlur?: boolean): void => {
		const parsedNumber = parseInt(n, 10);
		if (isNaN(parsedNumber) || (isBlur && parsedNumber === 0)) {
			this.numberOfDays = 1;
		} else {
			// the number should be less than 999 and greater than -1
			this.numberOfDays = Math.min(999, Math.max(0, parsedNumber));
		}
		this.onRangeChange(null, this.dynamicDateString, this.numberOfDays);
		this.numberOfDaysValue = `${this.numberOfDays}`;
	};

	/** set number of days from now */
	@action setNumberOfDaysOnBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
		const el: HTMLInputElement = e.currentTarget;
		this.setNumberOfDays(el.value, true);
	};

	/** set number of days from now */
	@action setNumberOfDaysOnChange = (e: React.FocusEvent<HTMLInputElement>): void => {
		const el: HTMLInputElement = e.currentTarget;
		this.numberOfDaysValue = el.value;
	};

	/** set the string that represents a preset date input */
	@action setPreSetDateString = (newPreSetDateString: string): void => {
		this.preSetDateString = newPreSetDateString;
	};

	/** handle changes to the time portion of the date range */
	@action handleTimeChange = (timeString: string, isToDate: boolean): void => {
		const { range } = this;
		const [time, period] = timeString.split(" ");
		const [hrs, minutes] = time.split(":").map((str) => parseInt(str, 10));
		const hours = period === "PM" ? (hrs % 12) + 12 : hrs % 12;
		const selected = isToDate ? range?.toDate : range?.fromDate;
		if (selected) {
			const newSelectedDate = new Date(
				selected.getFullYear(),
				selected.getMonth(),
				selected.getDate(),
				hours,
				minutes,
			);
			const newRange: C8DateRange = isToDate ?
				new C8DateRange(range.fromDate, newSelectedDate) :
				new C8DateRange(newSelectedDate, range.toDate);
			this.setRange(newRange);
		}
	};

	/** handle click on a day to set the from date of the range */
	@action handleDayClick = (date: Date): void => {
		const newRange = new C8DateRange(date);
		this.setRange(newRange);
	};

	/** handle selection of a date range from the picker */
	@action handleRangeSelectClick = (range: DateRangeDayPicker): void => {
		if (!range) {
			this.setRange(null);
			return;
		}

		const rangeTo = this.showTime ? range.to : range.to?.setHours(23, 59, 59, 999);

		const newRange = new C8DateRange(
			range.from,
			rangeTo,
		);
		this.setRange(newRange);

		const { timeSlotToRef } = this;
		// scroll down to the 11:30 PM time slot if applicable
		if (range.to && timeSlotToRef.current) {
			const { current } = timeSlotToRef;
			current.scrollTop = current.scrollHeight;
		}
	};

	/** convert a date range string to a DateRange object */
	@action stringToDateRange = (s: string): void => {
		const { dateStringFormat, isRangeMode } = this;
		this.setPreSetDateString("");
		const [startStr, endStr] = s.split(" - ");
		const startDate = parseDate(startStr, dateStringFormat);
		const endDate = parseDate(endStr, dateStringFormat);
		if (!isDateRangeValid(startDate, endDate, isRangeMode, MIN_YEAR, MAX_YEAR)) {
			this.setInvalidDateString(s);
		} else {
			const dateRange = new C8DateRange(startDate, endDate);
			this.setRange(dateRange);
		}
	};

	/** handle click on the clear button */
	@action clickClearButton = (e: React.MouseEvent<HTMLButtonElement>): void => {
		e.stopPropagation();
		this.onChangeInput(null, "", true);
	};

	/** handle changes to the input field and debounce the string to date range conversion */
	@action onChangeInput = (e: React.ChangeEvent<HTMLInputElement>, text?: string, closePopper?: boolean): void => {
		const inputString = text ?? e.currentTarget.value;
		if (!inputString) {
			this.setRange(new C8DateRange());
			if (closePopper) {
				this.closePopper();
			}
			return;
		}
		this.setPreSetDateString(inputString);
		this.debouncedStringToDateRange(inputString);
	};
}

export const DateRangeTimePickerStoreContext = createContext<DateRangeTimePickerStore>(undefined);
export const useDateRangeTimePickerStore = (): DateRangeTimePickerStore => useContext(DateRangeTimePickerStoreContext);
